// IncomingWebhookCredentialsModal.jsx

import React from 'react';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalActions from './ModalActions';

/**
 * IncomingWebhookCredentialsModal
 *
 * Renders one-time webhook credentials and payload field mapping details after creation.
 *
 * @param {object} theme - Active theme palette values.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Handler to close the modal.
 * @param {string} createdIncomingHookUrl - Newly created webhook url.
 * @param {string} createdIncomingHookSecret - Newly created webhook secret.
 * @param {string} createdIncomingHeaderPreview - Header preview including secret.
 * @param {array} createdIncomingFieldRows - Type fields available to map in payload.
 * @param {string} incomingSecretHeaderName - Required incoming secret header name.
 * @param {function} onCopyToClipboard - Handler to copy values.
 *
 * @returns {ReactNode} The rendered incoming webhook credentials modal.
 */
const IncomingWebhookCredentialsModal = ({
    theme,
    isOpen,
    onClose,
    createdIncomingHookUrl,
    createdIncomingHookSecret,
    createdIncomingHeaderPreview,
    createdIncomingFieldRows,
    incomingSecretHeaderName,
    onCopyToClipboard,
}) => {

    // ----- RENDER ----- //

    return (
        <Modal
            title="Webhook Credentials"
            isOpen={isOpen}
            onClose={onClose}
            width="620px"
            maxHeight="85vh"
        >
            <div className="api-integrations-modal-body">
                {/* INTRO */}
                <div
                    className="api-integrations-subtitle"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    Copy these now. The secret is only shown once.
                </div>

                {/* DETAILS */}
                <div className="api-integrations-form-grid">
                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Webhook URL
                    </div>
                    <div
                        className="api-integrations-static-field"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        {createdIncomingHookUrl || 'Unavailable'}
                    </div>

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Webhook Secret
                    </div>
                    <div
                        className="api-integrations-static-field"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        {createdIncomingHookSecret || 'Unavailable'}
                    </div>
                    <div
                        className="api-integrations-field-helper"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        This secret is shown only once. Save it now for your integration.
                    </div>

                    <div
                        className="api-integrations-field-label"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Required Header
                    </div>
                    <div
                        className="api-integrations-static-field api-integrations-code"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        {createdIncomingHeaderPreview}
                    </div>
                </div>

                {/* PAYLOAD FIELD MAP */}
                <div
                    className="api-integrations-field-map"
                    style={{
                        borderColor: theme.backgroundMuted,
                    }}
                >
                    <div
                        className="api-integrations-field-map-title"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        Available Payload Fields
                    </div>

                    {createdIncomingFieldRows.length === 0 && (
                        <div
                            className="api-integrations-empty"
                            style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            No fields found for this type.
                        </div>
                    )}

                    {createdIncomingFieldRows.map((field) => (
                        <div
                            key={field.fieldId}
                            className="api-integrations-field-map-row"
                            style={{
                                borderColor: theme.backgroundMuted,
                            }}
                        >
                            <div
                                className="api-integrations-field-map-slug"
                                style={{
                                    color: theme.foreground,
                                }}
                            >
                                {field.slug}
                            </div>
                            <div
                                className="api-integrations-field-map-meta"
                                style={{
                                    color: theme.foregroundMuted,
                                }}
                            >
                                {field.label} ({field.type})
                            </div>
                        </div>
                    ))}
                </div>

                {/* ACTIONS */}
                <ModalActions>
                    <Button
                        label="Copy URL"
                        onClick={() => onCopyToClipboard(createdIncomingHookUrl, 'Incoming webhook URL copied.')}
                        enabled={Boolean(createdIncomingHookUrl)}
                        height="30px"
                        fontSize="12px"
                        borderRadius="6px"
                    />
                    <Button
                        label="Copy Secret"
                        onClick={() => onCopyToClipboard(createdIncomingHookSecret, 'Incoming webhook secret copied.')}
                        enabled={Boolean(createdIncomingHookSecret)}
                        height="30px"
                        fontSize="12px"
                        borderRadius="6px"
                    />
                    <Button
                        label="Copy Header"
                        onClick={() => onCopyToClipboard(incomingSecretHeaderName, 'Incoming header name copied.')}
                        enabled={Boolean(incomingSecretHeaderName)}
                        height="30px"
                        fontSize="12px"
                        borderRadius="6px"
                    />
                    <Button
                        label="Copy Header + Secret"
                        onClick={() => onCopyToClipboard(createdIncomingHeaderPreview, 'Incoming header + secret copied.')}
                        enabled={Boolean(createdIncomingHookSecret)}
                        height="30px"
                        fontSize="12px"
                        borderRadius="6px"
                    />
                </ModalActions>
            </div>
        </Modal>
    );
};

// Dev Tools
IncomingWebhookCredentialsModal.displayName = 'IncomingWebhookCredentialsModal';

export default IncomingWebhookCredentialsModal;
